import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  /** 1-based index of the first visible row. Derived from page/pageSize when omitted. */
  rangeStart?: number;
  /** 1-based index of the last visible row. Derived from page/pageSize when omitted. */
  rangeEnd?: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** e.g. "transactions" — appended to the range readout. */
  noun?: string;
  className?: string;
}

const fmt = new Intl.NumberFormat('en-US');

const NAV_BTN = cn(
  'inline-flex h-[var(--size-control-sm)] w-[var(--size-control-sm)] items-center justify-center',
  'rounded-[var(--radius-control)] border border-border bg-surface-card text-ink-soft',
  'transition-colors hover:bg-cream-50 hover:text-ink',
  'disabled:pointer-events-none disabled:text-ink-disabled',
);

export default function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  rangeStart,
  rangeEnd,
  pageSizeOptions = [25, 50, 100],
  onPageChange,
  onPageSizeChange,
  noun,
  className,
}: PaginationProps) {
  const start = rangeStart ?? (total === 0 ? 0 : (page - 1) * pageSize + 1);
  const end = rangeEnd ?? Math.min(page * pageSize, total);

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-between gap-4', className)}
    >
      <label className="flex items-center gap-2 t-caption">
        Rows per page
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-[var(--size-control-sm)] rounded-[var(--radius-control)] border border-border-strong bg-surface-card px-2 text-xs text-ink"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      <div className="flex items-center gap-3">
        <span className="t-caption">
          {fmt.format(start)}&ndash;{fmt.format(end)} of {fmt.format(total)}
          {noun ? ` ${noun}` : ''}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={NAV_BTN}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            className={NAV_BTN}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </nav>
  );
}
